import { apiClient } from './apiClient';

export type EnterpriseCalendarSummary = {
  id: string;
  name: string;
  code?: string;
  description?: string;
  isGlobal: boolean;
  isActive: boolean;
  parentCalendarId?: string;
  parentCalendarName?: string;
  timeZoneId: string;
  eventCount: number;
  createdAtUtc: string;
};

export type CalendarWorkDay = {
  dayOfWeek: number;
  isWorkDay: boolean;
  workStartTime?: string;
  workEndTime?: string;
};

export type EnterpriseCalendarEvent = {
  id: string;
  name: string;
  description?: string;
  type: string;
  eventDate: string;
  isAllDay: boolean;
  startTime?: string;
  endTime?: string;
  recurrenceType: string;
  recurrenceInterval?: number;
  recurrenceDaysOfWeek?: string;
  isRecurringYearly: boolean;
  excludeFromSla: boolean;
  scope: string;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type EnterpriseCalendar = {
  id: string;
  name: string;
  code?: string;
  description?: string;
  isGlobal: boolean;
  isActive: boolean;
  parentCalendarId?: string;
  parentCalendarName?: string;
  timeZoneId: string;
  workDays: string;
  workStartTime: string;
  workEndTime: string;
  workDaySchedules: CalendarWorkDay[];
  events: EnterpriseCalendarEvent[];
  createdAtUtc: string;
  updatedAtUtc?: string;
};

export type EnterpriseCalendarListResult = {
  items: EnterpriseCalendarSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type EnterpriseCalendarUpsertPayload = {
  name: string;
  code?: string;
  description?: string;
  isActive?: boolean;
  timeZoneId?: string;
};

export type CloneEnterpriseCalendarPayload = {
  name: string;
  code?: string;
  description?: string;
  sourceCalendarId?: string;
  copyWorkSchedule?: boolean;
  copyEvents?: boolean;
};

export type CalendarWorkScheduleUpsertPayload = {
  workDays?: string;
  workStartTime?: string;
  workEndTime?: string;
  workDaySchedules?: CalendarWorkDay[];
};

export type EnterpriseCalendarEventUpsertPayload = {
  name: string;
  description?: string;
  type: string;
  eventDate: string;
  isAllDay?: boolean;
  startTime?: string;
  endTime?: string;
  recurrenceType?: string;
  recurrenceInterval?: number;
  recurrenceDaysOfWeek?: string;
  excludeFromSla?: boolean;
};

export type CalendarPlottedEvent = EnterpriseCalendarEvent & {
  calendarId: string;
  calendarName: string;
  color: string;
  eventSource?: 'calendar' | 'tasks';
  taskEndDate?: string;
  workItemId?: string;
  ownerUserId?: string;
  ownerUserName?: string;
  taskStatus?: string;
  taskPriority?: string;
  taskTitle?: string;
  taskCalendarIds?: string[];
  taskAreas?: string[];
  metaTitle?: string | null;
  marcoTitle?: string | null;
};

export async function listEnterpriseCalendars(params?: {
  page?: number;
  pageSize?: number;
}): Promise<EnterpriseCalendarListResult> {
  const response = await apiClient.get<EnterpriseCalendarListResult>('/v1/operations/calendars', {
    params,
  });
  return response.data;
}

export async function getEnterpriseCalendar(id: string): Promise<EnterpriseCalendar> {
  const response = await apiClient.get<EnterpriseCalendar>(`/v1/operations/calendars/${id}`);
  return response.data;
}

export async function getEffectiveEnterpriseCalendar(id: string): Promise<EnterpriseCalendar> {
  const response = await apiClient.get<EnterpriseCalendar>(`/v1/operations/calendars/${id}/effective`);
  return response.data;
}

export async function getTenantGlobalCalendar(): Promise<EnterpriseCalendar> {
  const response = await apiClient.get<EnterpriseCalendar>('/v1/operations/calendars/global');
  return response.data;
}

export async function cloneEnterpriseCalendar(
  payload: CloneEnterpriseCalendarPayload,
): Promise<EnterpriseCalendar> {
  const sourceCalendarId = isGuid(payload.sourceCalendarId) ? payload.sourceCalendarId : undefined;
  const response = await apiClient.post<EnterpriseCalendar>('/v1/operations/calendars/clone', {
    ...payload,
    sourceCalendarId,
  });
  return response.data;
}

function isGuid(value: string | undefined): value is string {
  return !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export async function updateEnterpriseCalendar(
  id: string,
  payload: EnterpriseCalendarUpsertPayload,
): Promise<EnterpriseCalendar> {
  const response = await apiClient.put<EnterpriseCalendar>(`/v1/operations/calendars/${id}`, payload);
  return response.data;
}

export async function updateCalendarWorkSchedule(
  id: string,
  payload: CalendarWorkScheduleUpsertPayload,
): Promise<EnterpriseCalendar> {
  const response = await apiClient.put<EnterpriseCalendar>(
    `/v1/operations/calendars/${id}/work-schedule`,
    payload,
  );
  return response.data;
}

export async function deleteEnterpriseCalendar(id: string): Promise<void> {
  await apiClient.delete(`/v1/operations/calendars/${id}`);
}

export async function createCalendarEvent(
  calendarId: string,
  payload: EnterpriseCalendarEventUpsertPayload,
): Promise<EnterpriseCalendarEvent> {
  const response = await apiClient.post<EnterpriseCalendarEvent>(
    `/v1/operations/calendars/${calendarId}/events`,
    payload,
  );
  return response.data;
}

export async function updateCalendarEvent(
  calendarId: string,
  eventId: string,
  payload: EnterpriseCalendarEventUpsertPayload,
): Promise<EnterpriseCalendarEvent> {
  const response = await apiClient.put<EnterpriseCalendarEvent>(
    `/v1/operations/calendars/${calendarId}/events/${eventId}`,
    payload,
  );
  return response.data;
}

export async function deleteCalendarEvent(calendarId: string, eventId: string): Promise<void> {
  await apiClient.delete(`/v1/operations/calendars/${calendarId}/events/${eventId}`);
}
